// New Conversation at a WhatsApp-only office (SMS_OUTBOUND_DISABLED), 2026-10-14.
//
// Texting is off, so a conversation can only be OPENED with a message WhatsApp has approved (Meta's policy:
// "You may only initiate conversations using an approved Message Template"; .claude-docs/whatsapp.md section 5).
// Staff can send one only from inside a thread ("Send an approved message"), and the office writes those on
// My Account > WhatsApp > "Add new message". So when the office has none, the New Conversation form (patient
// picker, phone box, a Start button that can never lead anywhere useful) is replaced by ONE card that says so
// and points at the tab. Offices that can text, and offices that already have an approved message, keep the form.
//
// ⛔ Keep this file IMPORT-FREE: cypress/node-tests/whatsapp-new-conversation-card.test.js executes this exact file.

export const NEW_CONVERSATION_VIEWS = Object.freeze({ FORM: 'form', LOADING: 'loading', EXPLAINER: 'explainer' });

/**
 * Which New Conversation view to draw.
 *
 * @param {object} p
 * @param {boolean} p.smsOutboundOff   a WhatsApp-only office (number present, texting off)
 * @param {'idle'|'loading'|'loaded'|'failed'} p.approvedStatus  state of the approved-messages request
 * @param {number} p.approvedCount     how many approved messages staff may send (only meaningful once loaded)
 * @returns {'form'|'loading'|'explainer'}
 */
export function resolveNewConversationView({ smsOutboundOff = false, approvedStatus, approvedCount } = {}) {
  if (!smsOutboundOff) return NEW_CONVERSATION_VIEWS.FORM;
  // A failed request is NOT proof the office has no messages: show the form, never hide it on a blip.
  if (approvedStatus === 'failed') return NEW_CONVERSATION_VIEWS.FORM;
  if (approvedStatus !== 'loaded') return NEW_CONVERSATION_VIEWS.LOADING;
  // Only a real, non-negative whole number can say "none". Anything else is not evidence of zero.
  if (!Number.isInteger(approvedCount) || approvedCount < 0) return NEW_CONVERSATION_VIEWS.FORM;
  return approvedCount === 0 ? NEW_CONVERSATION_VIEWS.EXPLAINER : NEW_CONVERSATION_VIEWS.FORM;
}

/**
 * Inside an open thread whose 24-hour window is closed: should the panel say "this office has no approved message
 * yet" and point at the tab? Only when the request FINISHED and returned a real zero. While it loads, or when it
 * failed, nothing is claimed (a blip must not tell an office with messages that it has none).
 */
export function showNoApprovedNotice({ approvedStatus, approvedCount } = {}) {
  return approvedStatus === 'loaded' && Number.isInteger(approvedCount) && approvedCount === 0;
}

/**
 * Who may open My Account > WhatsApp (where messages are written). MUST match MyAccount.vue's
 * `canRunWhatsAppWizard`; the test pins the two together. A link to a tab the person cannot see is a dead end.
 */
export function canAddWhatsAppMessages(role) {
  return role === 'Admin' || role === 'Super Admin' || role === 'Manager';
}

/** The router location of the tab (MyAccount.vue TAB_KEYS includes 'whatsapp'). */
export const WHATSAPP_TAB_LOCATION = Object.freeze({ name: 'MyAccount', query: Object.freeze({ tab: 'whatsapp' }) });

// ---- Words (customer-facing: plain language, no invented specifics) ----------------------------------------
export const EXPLAINER_TITLE = 'Start with an approved WhatsApp message';
export const EXPLAINER_INTRO =
  'Texting is turned off for this office, so patients are messaged on WhatsApp. WhatsApp only lets you message a patient first with a message it has approved. If a patient messages you first, you can reply for the next 24 hours.';
export const EXPLAINER_STEPS = Object.freeze([
  Object.freeze({ title: 'Write a message', body: 'In My Account, open the WhatsApp tab, choose Add new message and write what you want to say.' }),
  Object.freeze({ title: 'WhatsApp reviews it', body: 'Each message is checked before it can be used. This can take a day or more.' }),
  Object.freeze({ title: 'Send it from here', body: 'Once it is approved, come back here, pick the patient and choose your message.' }),
]);
/** The short version of the card, for the panel that replaces the message box in a thread (same button, same ask-an-admin line). */
export const PANEL_NONE_TEXT =
  'This office has no approved message for staff to send from a conversation yet. Once one is approved, you can send it to this patient from here. WhatsApp checks each message first, which can take a day or more.';
/**
 * Said on the card AND in the thread panel (Saumil, 2026-10-14, after Oral Care's My Account showed "Appointment
 * reminder: Approved"): an approved reminder looks like a message staff could use, so say plainly that it is not one.
 * Only messages written with "Add new message" can be sent from a conversation (the server refuses every other key).
 * The wording holds whether or not the office's reminder is approved, so no extra lookup is needed to show it.
 */
export const AUTOMATIC_NOTE = Object.freeze({
  lead: "Appointment reminders can't be used to start a conversation.",
  body: 'Even when WhatsApp has approved them, Teamio sends them for you automatically. Staff can\'t send them by hand from here.',
});
export const EXPLAINER_CTA = 'Add an approved message';
export const EXPLAINER_ASK_ADMIN = 'Ask an Admin or Manager to add one in My Account, on the WhatsApp tab.';
export const EXPLAINER_FINE_PRINT = 'Messages can include your practice name and phone number, but not patient details.';
export const EXPLAINER_CLOSE = 'Close';

/** The line on top of the form, at a WhatsApp-only office that already HAS an approved message. */
export const FORM_CALLOUT =
  'Texting is turned off for this office, so this conversation goes on WhatsApp. Start it, then choose one of your approved messages to send.';
